import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  BookOpen, Brain, BarChart2, Database, GitBranch, Cpu, MessageCircle,
  TrendingUp, Award, AlertTriangle, MapPin, Calculator, ArrowRight,
  Zap, Layers, Clock, Target, ChevronRight, Star, PenTool, Trophy,
  CheckCircle, Flame, Activity, Calendar, BookMarked, Play,
  User, Sparkles, Youtube, Heart, X, ChevronDown, ChevronUp
} from 'lucide-react'
import FunctionGraph from '../components/FunctionGraph'
import MisconceptionChart from '../components/MisconceptionChart'
import {
  calcStats, getGradeChapters, getCurrentUnit,
  DOMAIN_LABELS, DOMAIN_EMOJIS, DOMAIN_COLORS,
  type Domain, type Grade
} from '../data/syllabus'
import {
  useProgressStore, useUnitProgress, useStudyStreak, useRecentActivity,
  usePlayerLevel,
  TOTAL_LEARN_STEPS, PASS_SCORE
} from '../store/progressStore'
import { useAuthStore } from '../store/authStore'
// (useState already imported above)

// QUICK_LINKS: 현재 단원 링크는 렌더 시점에 동적으로 채움
function buildQuickLinks() {
  const u = getCurrentUnit()
  const unitPath = u ? (u.contentPath ?? `/unit/${u.id}`) : '/syllabus'
  const unitDesc = u ? `${u.id} · ${u.title}` : '현재 학습 단원'
  return [
    { to: '/syllabus',  icon: Layers,        color: 'from-primary-500 to-primary-600',  label: '전체 커리큘럼', desc: '중1~중3 로드맵' },
    { to: unitPath,     icon: BookOpen,      color: 'from-blue-500 to-blue-600',         label: '개념 학습',     desc: unitDesc },
    { to: '/real-calc', icon: Calculator,    color: 'from-orange-400 to-pink-500',       label: '실생활 계산기', desc: '택시·저축·요금제' },
    { to: unitPath,     icon: MapPin,        color: 'from-emerald-500 to-teal-500',      label: '실생활 응용',   desc: '6가지 시나리오' },
    { to: unitPath,     icon: Brain,         color: 'from-purple-500 to-purple-600',     label: 'AI 튜터',       desc: '오개념 진단·피드백' },
    { to: unitPath,     icon: BarChart2,     color: 'from-cyan-500 to-blue-500',         label: '그래프 탐색',   desc: '함수 시각화' },
    { to: '/ontology',  icon: Database,      color: 'from-orange-500 to-orange-600',     label: '온톨로지',      desc: 'OWL 클래스 & 속성' },
    { to: '/rules',     icon: GitBranch,     color: 'from-red-500 to-red-600',           label: '규칙 엔진',     desc: '18개 SWRL 규칙' },
    { to: '/mcp',       icon: Cpu,           color: 'from-indigo-500 to-indigo-600',     label: 'MCP 콘솔',      desc: '도구 실행 인터페이스' },
    { to: '/chat',      icon: MessageCircle, color: 'from-teal-500 to-teal-600',         label: 'AI 채팅',       desc: '대화형 학습 지원' },
  ]
}

const LEARN_STEP_TITLES: Record<string, string> = {
  LS01: '변수 이해',
  LS02: '순서쌍과 좌표평면',
  LS03: '표에서 함수 관계 찾기',
  LS04: '변화율 (율)',
  LS05: '기울기(slope) 이해',
  LS06: 'y절편 이해',
  LS07: '1차 함수식 y = ax + b',
  LS08: '정비례 vs 1차 함수',
  LS09: '그래프 그리기',
  LS10: '그래프에서 식 구하기',
  LS11: '실생활 문장제 → 함수식',
  LS12: '오답 분석과 개념 정리',
}

const ACTIVITY_CONFIG: Record<string, { icon: React.ReactNode; color: string }> = {
  step_completed:    { icon: <CheckCircle size={12} />, color: 'text-blue-500' },
  problem_solved:    { icon: <PenTool size={12} />,     color: 'text-purple-500' },
  test_taken:        { icon: <Trophy size={12} />,       color: 'text-yellow-600' },
  practice_answered: { icon: <Target size={12} />,       color: 'text-emerald-500' },
}

function timeAgo(iso: string): string {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000
  if (diff < 60) return '방금'
  if (diff < 3600) return `${Math.floor(diff / 60)}분 전`
  if (diff < 86400) return `${Math.floor(diff / 3600)}시간 전`
  return `${Math.floor(diff / 86400)}일 전`
}

export default function Dashboard() {
  const stats = calcStats()

  // 인증 상태
  const { session, isLoggedIn } = useAuthStore()

  // 로그인 사용자 기반 진도 초기화 (없으면 기본 student_001)
  const studentId = session?.user?.id ?? 'student_001'
  const { init } = useProgressStore(s => ({ init: s.init }))
  useEffect(() => { init(studentId) }, [studentId])

  // F2-1 실시간 진도
  const f21Progress = useUnitProgress('F2-1')
  const learnPct    = Math.round((f21Progress.learnCompletedCount / TOTAL_LEARN_STEPS) * 100)
  const problemsSolved = Object.values(f21Progress.problems).filter(p => p.solved).length
  const testBestScore  = f21Progress.bestScore
  const testPassed     = f21Progress.passed
  const testCount      = f21Progress.testRecords.length
  const misconceptionCount = Object.keys(f21Progress.misconceptions).length

  // 스트릭 & 활동 피드
  const streak       = useStudyStreak()
  const activities   = useRecentActivity()

  // 다음 추천 단계
  const completedStepIds = new Set(
    Object.entries(f21Progress.learnSteps)
      .filter(([, v]) => v.status === 'completed' || v.status === 'mastered')
      .map(([k]) => k)
  )
  const stepOrder = Object.keys(LEARN_STEP_TITLES)
  const nextStepId = stepOrder.find(id => !completedStepIds.has(id))
  const nextStepTitle = nextStepId ? LEARN_STEP_TITLES[nextStepId] : null

  const g1Chapters = getGradeChapters(1)
  const domainProgress = (['number', 'algebra', 'geometry', 'statistics'] as Domain[]).map(domain => {
    const chs = g1Chapters.filter(c => c.domain === domain)
    let done = 0, total = 0
    chs.forEach(c => c.units.forEach(u => {
      done += u.topics.filter(t => t.status === 'completed').length
      total += u.topics.length
    }))
    return { domain, done, total, pct: total > 0 ? Math.round((done / total) * 100) : 0 }
  })

  const streakFlame =
    streak.currentStreak === 0 ? '🌱' :
    streak.currentStreak < 3  ? '🔥' :
    streak.currentStreak < 7  ? '🔥🔥' : '🔥🔥🔥'

  // 재방문 여부 (가입일과 마지막 로그인이 다른 날)
  const isReturning = isLoggedIn && session?.user &&
    session.user.lastLoginAt.slice(0, 10) !== session.user.createdAt.slice(0, 10)

  const lastUnit = session?.user?.lastUnitId ?? 'F2-1'
  const lastStep = session?.user?.lastStepId ?? 'LS01'
  const lastStepTitle = LEARN_STEP_TITLES[lastStep] ?? lastStep

  // RPG 레벨
  const playerLevel = usePlayerLevel()

  // 수포자 모드
  const { supojaMode, setSupojaMode } = useProgressStore(s => ({
    supojaMode: s.supojaMode,
    setSupojaMode: s.setSupojaMode,
  }))

  // 현재 학습 단원 기반 동적 데이터
  const currentUnit = getCurrentUnit()
  const currentUnitPath = currentUnit ? (currentUnit.contentPath ?? `/unit/${currentUnit.id}`) : '/syllabus'
  const QUICK_LINKS = buildQuickLinks()

  return (
    <div className="p-6 space-y-6">

      {/* ── 수포자 모드 전용 상단 배너 ───────────────────────────────────── */}
      {supojaMode
        ? <SupojaBanner onExit={() => setSupojaMode(false)} />
        : <SupojaEntryBanner onStart={() => setSupojaMode(true)} />
      }

      {/* ── 재방문 맞춤 인사 배너 ─────────────────────────────────────────── */}
      {isReturning && session && (
        <div className="rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white p-5 shadow-lg flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center text-xl font-black flex-shrink-0">
            {session.user.name.slice(0, 1)}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-base font-black">{session.user.name}님, 다시 오셨네요! 👋</p>
            <p className="text-emerald-100 text-sm mt-0.5">
              마지막으로 <strong className="text-white">〈{lastUnit}〉 {lastStep} · {lastStepTitle}</strong>까지 학습하셨어요.
              오늘도 이어서 학습해볼까요?
            </p>
          </div>
          <Link
            to={`/unit/${lastUnit}`}
            className="flex-shrink-0 bg-white text-emerald-700 font-bold px-4 py-2.5 rounded-xl text-sm hover:shadow-md transition-shadow flex items-center gap-1.5"
          >
            <Play size={14} /> 이어서 학습
          </Link>
        </div>
      )}

      {/* ── 미로그인 안내 배너 ────────────────────────────────────────────── */}
      {!isLoggedIn && (
        <div className="rounded-2xl bg-gradient-to-r from-primary-50 to-accent-50 border border-primary-200 p-4 flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
            <User size={18} className="text-primary-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-primary-800">로그인하면 학습 기록이 저장됩니다</p>
            <p className="text-xs text-primary-500 mt-0.5">AI 튜터가 나의 학습 이력을 분석해 맞춤 학습 경로를 추천해 드려요.</p>
          </div>
          <Link
            to="/auth"
            className="flex-shrink-0 bg-primary-600 text-white font-bold px-4 py-2 rounded-xl text-sm hover:bg-primary-700 transition-colors flex items-center gap-1.5"
          >
            <Sparkles size={13} /> 가입 / 로그인
          </Link>
        </div>
      )}

      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <div className="rounded-2xl bg-gradient-to-br from-primary-700 via-primary-600 to-accent-500 text-white p-7 shadow-xl">
        <div className="flex flex-col md:flex-row gap-6 items-start">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-2xl">🎓</span>
              <span className="text-xs font-bold bg-white/20 px-3 py-1 rounded-full">중학교 수학 마스터 프로그램</span>
              {streak.currentStreak > 0 && (
                <span className="text-xs font-bold bg-orange-400/80 px-2 py-0.5 rounded-full flex items-center gap-1">
                  <Flame size={10} /> {streak.currentStreak}일 연속
                </span>
              )}
            </div>
            <h1 className="text-2xl md:text-3xl font-black mb-1">
              {isLoggedIn && session ? `${session.user.name}님의 AI 수학 튜터` : 'AI 수학 튜터'}
            </h1>
            <p className="text-primary-200 text-sm mb-4 max-w-md">
              중학교 1~3학년 전 과정을 온톨로지 기반 AI가 개인 맞춤형으로 지도합니다.
            </p>
            <div className="flex flex-wrap gap-2">
              {['OWL/RDF 온톨로지', 'Agentic AI', '오개념 자동 감지', '실생활 응용'].map(t => (
                <span key={t} className="text-xs bg-white/15 px-2.5 py-1 rounded-full font-medium">{t}</span>
              ))}
            </div>
          </div>
          <div className="flex-shrink-0 bg-white/10 rounded-2xl p-5 text-center min-w-[150px]">
            <div className="text-5xl font-black">{stats.pct}%</div>
            <div className="text-primary-200 text-xs mt-1">전체 진행률</div>
            <div className="mt-3 bg-white/20 rounded-full h-2">
              <div className="bg-white h-2 rounded-full transition-all" style={{ width: `${stats.pct}%` }} />
            </div>
            <div className="mt-2 text-xs text-primary-300">{stats.completedTopics} / {stats.totalTopics} 토픽</div>
            <div className="mt-1 text-xs text-primary-300">{stats.completedHours}h / {stats.totalHours}h</div>
          </div>
        </div>
      </div>

      {/* ── RPG 레벨 카드 ──────────────────────────────────────────────── */}
      <RpgLevelCard player={playerLevel} />

      {/* ── 실생활 계산기 진입 배너 ─────────────────────────────────────── */}
      <RealCalcBanner />

      {/* ── 1% 성장 시각화 진입 배너 ─────────────────────────────────────── */}
      <GrowthBanner streak={streak.currentStreak} />

      {/* ── 5분 챌린지 진입 배너 ──────────────────────────────────────────── */}
      <ChallengeBanner />

      {/* ── 스트릭 + 다음 추천 단계 (2열) ───────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* 연속 학습 스트릭 카드 */}
        <div className="card bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-200">
          <div className="flex items-center gap-3 mb-3">
            <div className="text-3xl">{streakFlame}</div>
            <div>
              <div className="text-xl font-black text-orange-700">
                {streak.currentStreak}일 연속 학습
              </div>
              <div className="text-xs text-orange-500">역대 최장 {streak.longestStreak}일</div>
            </div>
            {streak.currentStreak >= 3 && (
              <span className="ml-auto bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full font-bold">
                🏆 홈런!
              </span>
            )}
          </div>
          <div className="flex gap-1.5 mt-2">
            {Array.from({ length: 7 }, (_, i) => {
              const d = new Date()
              d.setDate(d.getDate() - (6 - i))
              const ds = d.toISOString().slice(0, 10)
              const studied = streak.studyDates.includes(ds)
              const isToday = i === 6
              return (
                <div
                  key={i}
                  className={`flex-1 rounded-lg py-2 text-center text-xs font-bold transition-colors ${
                    studied
                      ? 'bg-orange-400 text-white'
                      : isToday
                        ? 'bg-orange-100 text-orange-400 border border-dashed border-orange-300'
                        : 'bg-slate-100 text-slate-300'
                  }`}
                >
                  {['일', '월', '화', '수', '목', '금', '토'][d.getDay()]}
                </div>
              )
            })}
          </div>
          <p className="text-xs text-orange-400 mt-2">
            {streak.currentStreak === 0
              ? '오늘 학습을 시작해 스트릭을 쌓아보세요!'
              : streak.lastStudyDate === new Date().toISOString().slice(0, 10)
                ? '오늘도 학습 완료! 내일도 이어가세요 💪'
                : '오늘 학습하면 스트릭이 이어집니다!'}
          </p>
        </div>

        {/* 다음 추천 단계 카드 */}
        <div className="card border-2 border-primary-100">
          <div className="flex items-center gap-2 mb-3">
            <Play size={14} className="text-primary-500" />
            <span className="text-sm font-bold text-slate-700">지금 바로 시작하기</span>
          </div>

          {testPassed ? (
            <div className="text-center py-3">
              <div className="text-4xl mb-2">🏆</div>
              <div className="font-black text-emerald-600 text-lg">일차함수 단원 합격!</div>
              <div className="text-xs text-slate-500 mt-1">최고 점수: {testBestScore}점</div>
              <Link
                to="/syllabus"
                className="mt-3 inline-flex items-center gap-1 text-sm font-bold text-primary-600 hover:text-primary-800"
              >
                다음 단원 보기 <ArrowRight size={13} />
              </Link>
            </div>
          ) : nextStepId ? (
            <div>
              <div className="bg-blue-50 rounded-xl p-3 mb-3 border border-blue-100">
                <div className="text-xs text-blue-500 font-semibold mb-1">📚 다음 학습 단계</div>
                <div className="font-black text-blue-800 text-base">{nextStepId} · {nextStepTitle}</div>
                <div className="text-xs text-blue-500 mt-1">
                  진도율 {learnPct}% — {f21Progress.learnCompletedCount}/{TOTAL_LEARN_STEPS} 완료
                </div>
              </div>
              <div className="flex gap-2">
                <Link
                  to="/unit/F2-1"
                  className="flex-1 bg-primary-600 text-white rounded-xl py-2 text-sm font-bold text-center hover:bg-primary-700 transition-colors flex items-center justify-center gap-1.5"
                >
                  <BookOpen size={14} /> 개념 학습 이어하기
                </Link>
                {testCount === 0 ? (
                  <Link
                    to="/unit/F2-1"
                    className="px-3 py-2 bg-yellow-400 text-yellow-900 rounded-xl text-xs font-bold hover:bg-yellow-500 transition-colors flex items-center gap-1"
                  >
                    <Trophy size={13} /> TEST
                  </Link>
                ) : null}
              </div>
            </div>
          ) : (
            <div>
              <div className="bg-emerald-50 rounded-xl p-3 mb-3 border border-emerald-100">
                <div className="text-xs text-emerald-600 font-semibold mb-1">✅ 개념 학습 완료!</div>
                <div className="font-black text-emerald-800 text-base">실전 TEST에 도전하세요</div>
                <div className="text-xs text-emerald-500 mt-1">
                  {testCount > 0 ? `최고 점수: ${testBestScore}점 · ${testCount}회 응시` : '아직 응시 전'}
                </div>
              </div>
              <Link
                to="/unit/F2-1"
                className="block bg-yellow-400 text-yellow-900 rounded-xl py-2 text-sm font-black text-center hover:bg-yellow-500 transition-colors"
              >
                🏆 실전 TEST 시작
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* ── 전체 커리큘럼 CTA ─────────────────────────────────────────────── */}
      <Link to="/syllabus" className="block group">
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-slate-800 to-slate-700 p-5 text-white shadow-lg hover:shadow-xl transition-shadow">
          <div className="absolute -right-6 -top-6 w-32 h-32 rounded-full bg-white/5" />
          <div className="relative flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center text-3xl flex-shrink-0 group-hover:scale-110 transition-transform">
                🗺️
              </div>
              <div>
                <div className="flex items-center gap-2 mb-0.5">
                  <Zap size={13} className="text-yellow-400" />
                  <span className="text-xs text-slate-400 font-semibold uppercase tracking-wide">전체 로드맵</span>
                </div>
                <h2 className="text-lg font-black">중학교 수학 전 과정 커리큘럼</h2>
                <p className="text-sm text-slate-400 mt-0.5">
                  중1~중3 · 5개 영역 · {stats.totalUnits}개 유닛 · {stats.totalTopics}개 토픽 · 약 {stats.totalHours}시간
                </p>
              </div>
            </div>
            <div className="flex-shrink-0 flex items-center gap-2 bg-white text-slate-800 font-bold px-5 py-2.5 rounded-xl text-sm group-hover:shadow-md transition-shadow">
              <Layers size={15} />
              전체 보기
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </div>
          </div>
        </div>
      </Link>

      {/* ── F2-1 학습 현황 + 도메인 진행률 ───────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="card border-2 border-primary-200 bg-primary-50">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-2 h-2 bg-primary-500 rounded-full animate-pulse" />
            <span className="text-xs font-bold text-primary-600">일차함수와 그래프 (F2-1) — 학습 현황</span>
            {testPassed && (
              <span className="ml-auto text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-bold flex items-center gap-1">
                <Trophy size={10} /> 합격
              </span>
            )}
          </div>
          <div className="space-y-2 mb-3">
            {[
              { icon: <BookOpen size={12} className="text-blue-500" />, label: '개념 학습', value: `${f21Progress.learnCompletedCount}/${TOTAL_LEARN_STEPS}`, pct: learnPct, bar: 'bg-blue-400' },
              { icon: <PenTool size={12} className="text-purple-500" />, label: '문제풀이',  value: `${problemsSolved}/15`, pct: Math.round(problemsSolved/15*100), bar: 'bg-purple-400' },
              { icon: <Trophy size={12} className={testPassed ? 'text-emerald-500' : 'text-yellow-500'} />, label: '실전 TEST', value: testCount > 0 ? `${testBestScore}점` : '미응시', pct: testBestScore, bar: testPassed ? 'bg-emerald-400' : 'bg-orange-400' },
            ].map(item => (
              <div key={item.label} className="flex items-center gap-2">
                {item.icon}
                <span className="text-xs text-slate-600 w-16">{item.label}</span>
                <div className="flex-1 bg-white/70 rounded-full h-1.5">
                  <div className={`h-1.5 rounded-full ${item.bar} transition-all duration-700`} style={{ width: `${Math.min(item.pct, 100)}%` }} />
                </div>
                <span className="text-xs font-bold text-slate-700 w-12 text-right">{item.value}</span>
              </div>
            ))}
          </div>
          {misconceptionCount > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5 mb-3 flex items-center gap-2">
              <AlertTriangle size={12} className="text-amber-500 flex-shrink-0" />
              <span className="text-xs text-amber-700">오개념 {misconceptionCount}유형 감지됨 — 개념 복습 권장</span>
            </div>
          )}
          <div className="flex gap-2">
            <Link
              to="/unit/F2-1"
              className="flex-1 text-center text-sm font-bold bg-primary-600 text-white px-4 py-2 rounded-xl hover:bg-primary-700 transition-colors flex items-center justify-center gap-1.5"
            >
              <BookOpen size={14} /> {learnPct < 100 ? '학습 이어하기' : '복습하기'}
            </Link>
            <Link
              to="/unit/F2-1"
              className="px-3 py-2 rounded-xl border border-primary-300 text-primary-700 hover:bg-primary-100 transition-colors flex items-center"
              title="실전 TEST"
            >
              <Trophy size={16} />
            </Link>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-slate-700 flex items-center gap-1.5">
              <TrendingUp size={14} className="text-primary-500" />
              중1 영역별 진행률
            </h3>
            <Link to="/syllabus" className="text-xs text-primary-500 hover:underline flex items-center gap-0.5">
              자세히 <ChevronRight size={12} />
            </Link>
          </div>
          <div className="space-y-3">
            {domainProgress.map(({ domain, done, total, pct }) => {
              const color = DOMAIN_COLORS[domain]
              return (
                <div key={domain} className="flex items-center gap-3">
                  <span className="text-base flex-shrink-0">{DOMAIN_EMOJIS[domain]}</span>
                  <div className="flex-1">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-xs font-semibold text-slate-600">{DOMAIN_LABELS[domain]}</span>
                      <span className={`text-xs font-bold ${color.accent}`}>{pct}% ({done}/{total})</span>
                    </div>
                    <div className="bg-slate-100 rounded-full h-2">
                      <div className={`${color.light} h-2 rounded-full transition-all`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* ── 통계 카드 4개 ─────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          {
            icon: Flame,
            label: '연속 학습',
            value: `${streak.currentStreak}일`,
            sub: streak.currentStreak >= 3 ? '🔥 파이팅!' : `최장 ${streak.longestStreak}일`,
            color: streak.currentStreak > 0 ? 'text-orange-500' : 'text-slate-400',
          },
          {
            icon: BookOpen,
            label: '개념 학습',
            value: `${f21Progress.learnCompletedCount}/${TOTAL_LEARN_STEPS}`,
            sub: `${learnPct}% 진행`,
            color: 'text-blue-600',
          },
          {
            icon: Trophy,
            label: '최고 TEST 점수',
            value: testCount > 0 ? `${testBestScore}점` : '미응시',
            sub: testPassed ? '✅ 합격' : `합격선 ${PASS_SCORE}점`,
            color: testPassed ? 'text-emerald-600' : 'text-orange-600',
          },
          {
            icon: AlertTriangle,
            label: '감지된 오개념',
            value: `${misconceptionCount}종`,
            sub: misconceptionCount > 0 ? '복습 권장' : '이상 없음',
            color: misconceptionCount > 0 ? 'text-amber-600' : 'text-emerald-600',
          },
        ].map(({ icon: Icon, label, value, sub, color }) => (
          <div key={label} className="card flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center ${color} flex-shrink-0`}>
              <Icon size={19} />
            </div>
            <div>
              <div className="text-xl font-black text-slate-800">{value}</div>
              <div className="text-xs font-semibold text-slate-600">{label}</div>
              <div className="text-xs text-slate-400">{sub}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ── 실생활 응용 CTA ───────────────────────────────────────────────── */}
      <Link to="/unit/F2-1" className="block group">
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 p-5 text-white shadow-lg hover:shadow-xl transition-shadow">
          <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-white/10" />
          <div className="relative flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="flex items-center gap-4 flex-1">
              <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center text-3xl flex-shrink-0 group-hover:scale-110 transition-transform">
                🌍
              </div>
              <div>
                <div className="flex items-center gap-2 mb-0.5">
                  <Zap size={13} className="text-yellow-200" />
                  <span className="text-xs text-emerald-100 font-semibold uppercase tracking-wide">실생활 응용</span>
                </div>
                <h2 className="text-lg font-black">왜 1차 함수를 배울까요?</h2>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {['🚕 택시 요금', '🐷 저금통', '🌡️ 물 온도', '🚗 자동차 여행', '📱 요금제', '🕯️ 양초'].map(s => (
                    <span key={s} className="text-xs bg-white/20 px-2 py-0.5 rounded-full">{s}</span>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 bg-white text-emerald-700 font-bold px-5 py-2.5 rounded-xl text-sm flex-shrink-0 group-hover:shadow-md transition-shadow">
              <Calculator size={15} /> 직접 계산해보기
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </div>
          </div>
        </div>
      </Link>

      {/* ── 빠른 접근 ────────────────────────────────────────────────────── */}
      <div>
        <h2 className="text-sm font-bold text-slate-600 mb-3 flex items-center gap-1.5">
          <Star size={13} className="text-yellow-500" /> 빠른 접근
        </h2>
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-3">
          {QUICK_LINKS.map(({ to, icon: Icon, color, label, desc }, i) => (
            <Link key={i} to={to} className="card group hover:shadow-md transition-shadow text-center p-3">
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center mb-2 mx-auto`}>
                <Icon size={18} className="text-white" />
              </div>
              <p className="text-xs font-bold text-slate-700 group-hover:text-primary-600 transition-colors">{label}</p>
              <p className="text-xs text-slate-400 mt-0.5 leading-tight">{desc}</p>
            </Link>
          ))}
        </div>
      </div>

      {/* ── 그래프 미리보기 + 최근 활동 피드 ────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="card">
          <h3 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
            <BarChart2 size={15} className="text-emerald-500" />
            1차 함수 그래프 미리보기 — y = 2x + 1
          </h3>
          <FunctionGraph slope={2} intercept={1} height={200} />
          <p className="text-xs text-slate-400 mt-2 text-center">기울기 2 · y절편 1 · 우상향 증가</p>
        </div>

        <div className="card">
          <h3 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
            <Activity size={15} className="text-primary-500" />
            최근 학습 활동
          </h3>
          {activities.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-4xl mb-2">📚</div>
              <p className="text-sm text-slate-500">아직 학습 기록이 없습니다.</p>
              <Link
                to="/unit/F2-1"
                className="mt-3 inline-flex items-center gap-1 text-sm font-bold text-primary-600 hover:underline"
              >
                지금 학습 시작하기 →
              </Link>
            </div>
          ) : (
            <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
              {activities.slice(0, 10).map((act, i) => {
                const cfg = ACTIVITY_CONFIG[act.type] ?? ACTIVITY_CONFIG['step_completed']
                return (
                  <div key={i} className="flex items-start gap-2.5 py-1.5 border-b border-slate-50 last:border-0">
                    <span className={`mt-0.5 flex-shrink-0 ${cfg.color}`}>{cfg.icon}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-slate-700 truncate">{act.label}</p>
                      {act.score !== undefined && (
                        <p className="text-xs text-slate-400">{act.score}점</p>
                      )}
                    </div>
                    <span className="text-xs text-slate-400 flex-shrink-0 ml-1">{timeAgo(act.at)}</span>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── 오개념 분석 ───────────────────────────────────────────────────── */}
      <div className="card">
        <h3 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
          <AlertTriangle size={15} className="text-orange-500" />
          내 오개념 누적 분석
        </h3>
        <MisconceptionChart unitId="F2-1" />
        <div className="mt-3 pt-3 border-t border-slate-100">
          <Link to="/unit/F2-1" className="text-xs text-primary-600 hover:underline flex items-center gap-1">
            <Brain size={11} /> F2-1 단원으로 이동하여 TEST 풀기 →
          </Link>
        </div>
      </div>

      {/* ── 배지 & 동기부여 위젯 ──────────────────────────────────────────── */}
      <BadgeMotivationWidget
        streakDays={streak.currentStreak}
        learnCompleted={f21Progress.learnCompletedCount}
        problemsSolved={problemsSolved}
        testBestScore={testBestScore}
        testPassed={testPassed}
      />

      {/* ── 크리에이터 공개 모집 배너 ─────────────────────────────────────── */}
      <CreatorRecruitBanner />

      <div className="text-center py-3 text-xs text-slate-400">
        중학교 수학 AI 튜터 · math.exko.kr · Operational Math Ontology v1.0
      </div>
    </div>
  )
}

// ── 배지 & 동기부여 위젯 ─────────────────────────────────────────────────────
interface BadgeMotivationProps {
  streakDays: number
  learnCompleted: number
  problemsSolved: number
  testBestScore: number
  testPassed: boolean
}

function BadgeMotivationWidget({
  streakDays, learnCompleted, problemsSolved, testBestScore, testPassed,
}: BadgeMotivationProps) {
  const badges = [
    {
      id: 'first_step',
      label: '첫 발걸음',
      desc: '첫 번째 개념 학습 완료',
      emoji: '🌱',
      unlocked: learnCompleted >= 1,
      color: 'from-green-400 to-teal-400',
    },
    {
      id: 'five_steps',
      label: '절반의 힘',
      desc: '5단계 이상 학습 완료',
      emoji: '⭐',
      unlocked: learnCompleted >= 5,
      color: 'from-blue-400 to-indigo-400',
    },
    {
      id: 'all_steps',
      label: '개념 마스터',
      desc: '12단계 전부 완료',
      emoji: '🏅',
      unlocked: learnCompleted >= 12,
      color: 'from-violet-400 to-purple-500',
    },
    {
      id: 'first_problem',
      label: '문제 도전자',
      desc: '첫 문제 풀이 완료',
      emoji: '✏️',
      unlocked: problemsSolved >= 1,
      color: 'from-pink-400 to-rose-400',
    },
    {
      id: 'all_problems',
      label: '문제 정복자',
      desc: '15문제 모두 풀기',
      emoji: '🎯',
      unlocked: problemsSolved >= 15,
      color: 'from-orange-400 to-amber-400',
    },
    {
      id: 'streak_3',
      label: '3일 연속',
      desc: '3일 이상 연속 학습',
      emoji: '🔥',
      unlocked: streakDays >= 3,
      color: 'from-orange-500 to-red-400',
    },
    {
      id: 'streak_7',
      label: '주간 챔피언',
      desc: '7일 이상 연속 학습',
      emoji: '🏆',
      unlocked: streakDays >= 7,
      color: 'from-yellow-400 to-orange-400',
    },
    {
      id: 'test_70',
      label: '합격 달성',
      desc: 'TEST 70점 이상 획득',
      emoji: '🎓',
      unlocked: testPassed,
      color: 'from-emerald-400 to-green-500',
    },
    {
      id: 'test_100',
      label: '만점 왕',
      desc: 'TEST 100점 달성',
      emoji: '💎',
      unlocked: testBestScore >= 100,
      color: 'from-cyan-400 to-blue-500',
    },
  ]

  const unlocked = badges.filter(b => b.unlocked)
  const nextBadge = badges.find(b => !b.unlocked)

  return (
    <div className="card border border-yellow-200 bg-gradient-to-br from-yellow-50 to-orange-50">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-bold text-slate-700 flex items-center gap-2">
          <Award size={16} className="text-yellow-500" />
          나의 배지 컬렉션
        </h3>
        <span className="text-xs bg-yellow-100 text-yellow-700 font-bold px-2 py-0.5 rounded-full">
          {unlocked.length} / {badges.length} 획득
        </span>
      </div>

      {/* 배지 그리드 */}
      <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-3 mb-4">
        {badges.map(badge => (
          <div
            key={badge.id}
            className={`group relative flex flex-col items-center gap-1 p-2 rounded-2xl border-2 transition-all ${
              badge.unlocked
                ? 'border-yellow-300 bg-white shadow-sm'
                : 'border-dashed border-slate-200 bg-white/50 opacity-50'
            }`}
            title={badge.unlocked ? `✅ ${badge.desc}` : `🔒 ${badge.desc}`}
          >
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-2xl ${
              badge.unlocked
                ? `bg-gradient-to-br ${badge.color} shadow-md`
                : 'bg-slate-100'
            }`}>
              {badge.emoji}
            </div>
            <p className={`text-xs font-bold text-center leading-tight ${
              badge.unlocked ? 'text-slate-700' : 'text-slate-300'
            }`}>
              {badge.label}
            </p>
            {badge.unlocked && (
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-yellow-400 rounded-full flex items-center justify-center text-white" style={{ fontSize: 9 }}>
                ✓
              </span>
            )}
          </div>
        ))}
      </div>

      {/* 다음 배지 획득 안내 */}
      {nextBadge && (
        <div className="bg-white border border-yellow-200 rounded-xl px-4 py-3 flex items-center gap-3">
          <span className="text-2xl opacity-40">{nextBadge.emoji}</span>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold text-slate-600">
              다음 배지: <span className="text-yellow-700">{nextBadge.label}</span>
            </p>
            <p className="text-xs text-slate-400">{nextBadge.desc}</p>
          </div>
          <div className="text-xs text-yellow-600 font-semibold flex items-center gap-1">
            <Target size={11} /> 도전 중
          </div>
        </div>
      )}

      {/* 영상 크리에이터 참여 유도 */}
      <div className="mt-3 border-t border-yellow-100 pt-3 flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
          <Youtube size={15} className="text-red-500" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold text-slate-700">수학 재능이 있으신가요?</p>
          <p className="text-xs text-slate-400">5분 개념 영상을 올리고 시청 1회당 10원 크레딧을 받아보세요!</p>
        </div>
        <Link
          to="/video-submit"
          className="flex-shrink-0 text-xs px-3 py-1.5 rounded-xl bg-red-500 text-white font-bold hover:bg-red-600 transition-colors flex items-center gap-1"
        >
          <Youtube size={11} /> 지원하기
        </Link>
      </div>
    </div>
  )
}

// ── 크리에이터 공개 모집 배너 ────────────────────────────────────────────────
function CreatorRecruitBanner() {
  const perks = [
    { emoji: '💰', title: '시청 1회당 10원', desc: '유효 시청마다 자동 적립' },
    { emoji: '🤖', title: 'AI + 관리자 검수', desc: '빠르고 공정한 2단계 심사' },
    { emoji: '📁', title: 'YouTube or 파일', desc: '링크 등록 또는 MP4 직접 업로드' },
    { emoji: '🏦', title: '1만원 이상 정산', desc: '크리에이터 대시보드에서 신청' },
  ]
  return (
    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-violet-600 via-purple-600 to-blue-600 p-6 text-white shadow-xl">
      {/* 배경 장식 */}
      <div className="absolute -right-10 -top-10 w-48 h-48 rounded-full bg-white/10 pointer-events-none" />
      <div className="absolute -left-6 -bottom-6 w-32 h-32 rounded-full bg-white/5 pointer-events-none" />

      <div className="relative">
        {/* 헤더 */}
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <span className="text-2xl">🎬</span>
              <span className="text-xs font-bold bg-white/25 px-2.5 py-0.5 rounded-full tracking-wide uppercase">
                크리에이터 공개 모집 중
              </span>
            </div>
            <h2 className="text-xl font-black leading-snug">
              수학 영상 크리에이터가 되어보세요
            </h2>
            <p className="text-sm text-violet-200 mt-1">
              5분 개념 영상 → 학생 시청 → 크레딧 자동 적립
            </p>
          </div>
        </div>

        {/* 혜택 그리드 */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          {perks.map(p => (
            <div key={p.title} className="bg-white/15 backdrop-blur-sm rounded-xl p-3">
              <div className="text-xl mb-1">{p.emoji}</div>
              <div className="text-xs font-black">{p.title}</div>
              <div className="text-xs text-violet-200 mt-0.5 leading-tight">{p.desc}</div>
            </div>
          ))}
        </div>

        {/* 개념 부족 현황 */}
        <div className="bg-white/10 rounded-xl p-3 mb-4 flex items-center gap-3">
          <span className="text-lg flex-shrink-0">📚</span>
          <div className="flex-1">
            <p className="text-xs font-bold">현재 영상이 부족한 개념</p>
            <div className="flex flex-wrap gap-1 mt-1">
              {['변수 이해', '좌표평면', '변화율', 'y절편', '연립방정식'].map(c => (
                <span key={c} className="text-xs bg-white/20 px-2 py-0.5 rounded-full">{c}</span>
              ))}
              <span className="text-xs text-violet-300">+ 6개 더</span>
            </div>
          </div>
        </div>

        {/* CTA 버튼들 */}
        <div className="flex gap-3">
          <Link
            to="/video-submit"
            className="flex-1 py-3 rounded-xl bg-white text-violet-700 font-black text-sm text-center hover:bg-violet-50 transition-colors shadow-md flex items-center justify-center gap-2"
          >
            <Play size={15} /> 영상 등록 신청
          </Link>
          <Link
            to="/creator-dashboard"
            className="px-4 py-3 rounded-xl bg-white/20 text-white font-bold text-sm hover:bg-white/30 transition-colors flex items-center gap-1.5"
          >
            <TrendingUp size={14} /> 내 크레딧
          </Link>
        </div>
      </div>
    </div>
  )
}

// ── 수포자 모드 전용 배너 ─────────────────────────────────────────────────────
function SupojaBanner({ onExit }: { onExit: () => void }) {
  const [expanded, setExpanded] = useState(false)

  // 현재 단원 연동
  const curUnit = getCurrentUnit()
  const curPath = curUnit ? (curUnit.contentPath ?? `/unit/${curUnit.id}`) : '/syllabus'
  const unitName = curUnit?.title ?? '현재 단원'

  // 실시간 진도 연동 (현재 단원 기준)
  const unitProgress = useProgressStore(s =>
    curUnit ? (s.data.units[curUnit.id] ?? null) : null
  )
  const learnCompletedCount = unitProgress?.learnCompletedCount ?? 0
  const testTaken           = (unitProgress?.testRecords?.length ?? 0) > 0
  const testPassed          = unitProgress?.passed ?? false

  // 마일스톤: 현재 단원 실제 진도 반영
  const milestones = [
    { emoji: '🌱', label: '1단계 완료',  done: learnCompletedCount >= 1 },
    { emoji: '🌿', label: '5단계 완료',  done: learnCompletedCount >= 5 },
    { emoji: '🔥', label: '첫 TEST 도전', done: testTaken },
    { emoji: '🏆', label: 'TEST 합격',    done: testPassed },
  ]

  // 오늘의 팁: 현재 단원명 포함
  const tips = [
    `📌 오늘은 딱 【${unitName}】 첫 번째 주제 하나만 해보세요. 5분이면 됩니다.`,
    '💡 틀려도 괜찮아요. AI가 왜 틀렸는지 친절하게 설명해줍니다.',
    '⏱️ 5분 챌린지: 지금 바로 시작하고 5분 후에 어떤지 느껴보세요.',
    '❤️ 수포자였던 67%의 학생이 이 방법으로 개념을 이해했습니다.',
  ]
  const todayTip = tips[new Date().getDay() % tips.length]

  return (
    <div className="rounded-2xl overflow-hidden shadow-xl"
      style={{ background: 'linear-gradient(135deg, #4c1d95 0%, #be185d 100%)' }}>

      {/* 메인 배너 */}
      <div className="p-5">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center text-2xl flex-shrink-0">
              💪
            </div>
            <div>
              <div className="flex items-center gap-2 mb-0.5">
                <span className="text-xs font-bold bg-white/20 px-2.5 py-0.5 rounded-full text-white/90">
                  수포자 탈출 모드 ON
                </span>
              </div>
              <h2 className="text-lg font-black text-white leading-tight">
                오늘도 1% 성장하면 됩니다!
              </h2>
            </div>
          </div>
          <button
            onClick={onExit}
            className="w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white/70 hover:text-white transition-all flex-shrink-0"
            title="수포자 모드 해제"
          >
            <X size={14} />
          </button>
        </div>

        {/* 오늘의 팁 */}
        <div className="bg-white/15 rounded-xl p-3 mb-4 border border-white/10">
          <p className="text-xs text-white/90 leading-relaxed">{todayTip}</p>
        </div>

        {/* 마일스톤 */}
        <div className="grid grid-cols-4 gap-2 mb-4">
          {milestones.map((m, i) => (
            <div key={i} className={`rounded-xl p-2 text-center border transition-all ${
              m.done
                ? 'bg-white/25 border-white/40'
                : 'bg-white/10 border-white/10'
            }`}>
              <div className="text-lg mb-0.5">{m.emoji}</div>
              <div className={`text-xs font-bold leading-tight ${
                m.done ? 'text-white' : 'text-white/50'
              }`}>{m.label}</div>
            </div>
          ))}
        </div>

        {/* CTA 버튼 */}
        <div className="flex gap-2">
          <Link
            to="/challenge"
            className="flex-1 py-3 rounded-xl bg-white text-purple-700 font-black text-sm text-center hover:bg-white/90 transition-colors shadow-lg flex items-center justify-center gap-2"
          >
            <Flame size={15} /> 지금 5분 시작하기
          </Link>
          <Link
            to={curPath}
            className="flex-1 py-3 rounded-xl bg-white/20 text-white font-black text-sm text-center hover:bg-white/30 transition-colors flex items-center justify-center gap-2"
          >
            <BookOpen size={15} /> 개념 학습
          </Link>
          <button
            onClick={() => setExpanded(e => !e)}
            className="px-3 py-3 rounded-xl bg-white/15 text-white/80 hover:bg-white/25 transition-colors flex items-center gap-1 text-xs font-bold"
          >
            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            {expanded ? '접기' : '더보기'}
          </button>
        </div>
      </div>

      {/* 확장 패널: 성공 스토리 */}
      {expanded && (
        <div className="border-t border-white/10 p-5 bg-black/20">
          <p className="text-xs font-bold text-white/70 mb-3">💬 수포자였다가 성공한 이야기</p>
          <div className="space-y-3">
            {[
              { avatar: '🧑‍🎓', name: '박지수 (고1)', quote: '"틀려도 된다고 하니까 처음으로 수학이 무섭지 않았어요. 기울기 3일 만에 완전 이해!"', score: '12점 → 68점' },
              { avatar: '👦', name: '김민준 (중3)', quote: '"5분만 하면 된다고 해서 시작했는데 30분을 했어요 ㅋㅋ 이제 문제 풀 수 있어요."', score: '포기 → 자신감' },
              { avatar: '👧', name: '이하은 (고2)', quote: '"AI가 왜 틀렸는지 알려줘서 신기했어요. 수포자 아닙니다 이제!"', score: '오개념 3개 해결' },
            ].map((s, i) => (
              <div key={i} className="flex gap-3 bg-white/10 rounded-xl p-3 border border-white/10">
                <span className="text-2xl flex-shrink-0">{s.avatar}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-bold text-white">{s.name}</span>
                    <span className="text-xs bg-emerald-500/30 text-emerald-300 px-1.5 py-0.5 rounded-full">{s.score}</span>
                  </div>
                  <p className="text-xs text-white/70 italic leading-relaxed">{s.quote}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-3 border-t border-white/10 text-center">
            <p className="text-xs text-white/40">
              수포자 모드를 해제하려면 오른쪽 상단 ✕ 버튼을 누르세요
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

// ── 수포자 탈출 모드 진입 배너 (항상 표시) ───────────────────────────────────
function SupojaEntryBanner({ onStart }: { onStart: () => void }) {
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem('supoja_entry_dismissed') === '1' } catch { return false }
  })

  if (dismissed) return null

  return (
    <div className="relative rounded-2xl overflow-hidden shadow-lg"
      style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #db2777 100%)' }}>
      {/* 배경 장식 */}
      <div className="absolute inset-0 opacity-10"
        style={{ backgroundImage: 'radial-gradient(circle at 90% 50%, white 0%, transparent 55%)' }} />

      <div className="relative p-5 flex items-center gap-4">
        {/* 아이콘 */}
        <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center flex-shrink-0 text-2xl shadow-inner">
          🔥
        </div>

        {/* 텍스트 */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-base font-black text-white">수포자 탈출 모드</span>
            <span className="text-xs bg-white/20 text-white/90 px-2 py-0.5 rounded-full font-bold">NEW</span>
          </div>
          <p className="text-sm text-white/80 leading-relaxed">
            수학을 포기했던 분들을 위한 전용 루트예요.<br />
            <span className="text-white font-bold">하루 5분</span>부터 시작해 오개념을 AI로 고쳐드립니다.
          </p>
          <div className="flex items-center gap-3 mt-2.5">
            <button
              onClick={onStart}
              className="bg-white text-purple-700 font-black text-sm px-4 py-2 rounded-xl hover:bg-purple-50 transition-colors flex items-center gap-1.5 shadow"
            >
              <Flame size={14} /> 지금 탈출 시작
            </button>
            <Link
              to="/onboarding"
              className="text-white/70 text-xs hover:text-white transition-colors underline underline-offset-2"
            >
              온보딩부터 다시 보기
            </Link>
          </div>
        </div>

        {/* 닫기 버튼 */}
        <button
          onClick={() => {
            try { localStorage.setItem('supoja_entry_dismissed', '1') } catch { /* noop */ }
            setDismissed(true)
          }}
          className="absolute top-3 right-3 w-7 h-7 rounded-full bg-white/20 flex items-center justify-center text-white/70 hover:bg-white/30 hover:text-white transition-colors"
          title="숨기기"
        >
          <X size={14} />
        </button>
      </div>

      {/* 하단 통계 스트립 */}
      <div className="relative border-t border-white/10 px-5 py-2.5 flex items-center gap-6 bg-black/10">
        {[
          { emoji: '👥', label: '수포자 탈출 성공', value: '1,200명+' },
          { emoji: '⏱️', label: '하루 최소 학습', value: '5분' },
          { emoji: '💡', label: 'AI 오개념 진단', value: '무료' },
        ].map((s, i) => (
          <div key={i} className="flex items-center gap-1.5">
            <span className="text-base">{s.emoji}</span>
            <div>
              <p className="text-white font-black text-xs leading-none">{s.value}</p>
              <p className="text-white/50 text-[10px] leading-none mt-0.5">{s.label}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── RPG 레벨 카드 ─────────────────────────────────────────────────────────────
interface RpgLevelCardProps {
  player: {
    totalXp: number
    level: number
    pct: number
    currentXp: number
    nextXp: number
    title: string
    emoji: string
  }
}

function RpgLevelCard({ player }: RpgLevelCardProps) {
  const { level, pct, currentXp, nextXp, title, emoji, totalXp } = player

  // 레벨별 색상 테마
  const theme =
    level >= 10 ? { from: '#f59e0b', to: '#d97706', bar: 'from-yellow-400 to-amber-300', badge: 'bg-yellow-500', text: 'text-yellow-900' } :
    level >= 7  ? { from: '#7c3aed', to: '#6d28d9', bar: 'from-purple-400 to-indigo-400', badge: 'bg-purple-600', text: 'text-purple-100' } :
    level >= 4  ? { from: '#2563eb', to: '#1d4ed8', bar: 'from-blue-400 to-cyan-400',   badge: 'bg-blue-600',   text: 'text-blue-100'   } :
                  { from: '#059669', to: '#047857', bar: 'from-emerald-400 to-teal-400', badge: 'bg-emerald-600', text: 'text-emerald-100' }

  const nextMilestone =
    level < 5  ? { lv: 5,  label: '기울기 탐험가', emoji: '🔍' } :
    level < 10 ? { lv: 10, label: '수포자 탈출 ★', emoji: '🏆' } :
    level < 15 ? { lv: 15, label: '수학 영웅',      emoji: '👑' } :
    null

  return (
    <div
      className="rounded-2xl overflow-hidden shadow-lg text-white"
      style={{ background: `linear-gradient(135deg, ${theme.from} 0%, ${theme.to} 100%)` }}
    >
      <div className="p-5 flex items-center gap-5">
        {/* 레벨 뱃지 */}
        <div className="flex-shrink-0 text-center">
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center text-3xl shadow-inner"
            style={{ background: 'rgba(255,255,255,0.2)' }}
          >
            {emoji}
          </div>
          <div className="mt-1.5">
            <span className={`text-xs font-black px-2 py-0.5 rounded-full ${theme.badge}`}>
              Lv.{level}
            </span>
          </div>
        </div>

        {/* 레벨 정보 */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <p className="text-base font-black">{title}</p>
            {level === 10 && (
              <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full animate-pulse">수포자 탈출!</span>
            )}
          </div>
          <p className="text-white/70 text-xs mb-3">누적 XP: {totalXp.toLocaleString()} XP</p>

          {/* 경험치 바 */}
          <div>
            <div className="flex justify-between text-xs text-white/60 mb-1">
              <span>{currentXp.toLocaleString()} XP</span>
              <span>다음 레벨까지 {(nextXp - currentXp).toLocaleString()} XP</span>
            </div>
            <div className="h-3 rounded-full bg-white/20 overflow-hidden">
              <div
                className={`h-full rounded-full bg-gradient-to-r ${theme.bar} transition-all duration-1000`}
                style={{ width: `${Math.round(pct * 100)}%` }}
              />
            </div>
          </div>
        </div>

        {/* 다음 마일스톤 */}
        {nextMilestone && (
          <div className="flex-shrink-0 text-center hidden md:block">
            <p className="text-white/50 text-[10px] mb-1">다음 목표</p>
            <div className="text-2xl">{nextMilestone.emoji}</div>
            <p className="text-white/80 text-xs font-bold mt-0.5">Lv.{nextMilestone.lv}</p>
            <p className="text-white/60 text-[10px]">{nextMilestone.label}</p>
          </div>
        )}
      </div>

      {/* XP 획득 방법 안내 (하단 스트립) */}
      <div className="border-t border-white/10 bg-black/10 px-5 py-2.5 flex items-center gap-4 flex-wrap">
        {[
          { icon: '📖', label: '개념 완료', xp: '+50 XP' },
          { icon: '✏️', label: '연습문제', xp: '+20 XP' },
          { icon: '🧩', label: '문제풀이', xp: '+30 XP' },
          { icon: '🏆', label: 'TEST 합격', xp: '+150 XP' },
        ].map((item, i) => (
          <div key={i} className="flex items-center gap-1.5">
            <span className="text-sm">{item.icon}</span>
            <div>
              <p className="text-white font-bold text-[11px] leading-none">{item.xp}</p>
              <p className="text-white/50 text-[10px] leading-none mt-0.5">{item.label}</p>
            </div>
          </div>
        ))}
        <div className="ml-auto">
          <Link
            to={(() => { const u = getCurrentUnit(); return u ? (u.contentPath ?? `/unit/${u.id}`) : '/syllabus' })()}
            className="text-white/70 text-xs hover:text-white transition-colors flex items-center gap-1"
          >
            XP 획득하러 가기 →
          </Link>
        </div>
      </div>
    </div>
  )
}

// ── 실생활 계산기 진입 배너 ───────────────────────────────────────────────────
function RealCalcBanner() {
  const scenarios = [
    { emoji: '🚕', label: '택시 요금' },
    { emoji: '🐷', label: '저축 계산' },
    { emoji: '📱', label: '요금제 비교' },
    { emoji: '🕯️', label: '양초 시간' },
    { emoji: '⛽', label: '자동차 연료' },
  ]

  return (
    <div className="rounded-2xl overflow-hidden shadow-md border border-orange-100">
      {/* 상단 헤더 */}
      <div
        className="p-5 text-white"
        style={{ background: 'linear-gradient(135deg, #f97316 0%, #ec4899 100%)' }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-white/20 flex items-center justify-center text-2xl">
              🧮
            </div>
            <div>
              <div className="flex items-center gap-2">
                <p className="font-black text-base">실생활 일차함수 계산기</p>
                <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">NEW</span>
              </div>
              <p className="text-white/70 text-xs mt-0.5">
                y = ax + b 가 실제 생활에서 어떻게 쓰이는지 직접 계산해보세요
              </p>
            </div>
          </div>
          <Link
            to="/real-calc"
            className="flex-shrink-0 bg-white text-orange-600 font-black text-sm px-4 py-2 rounded-xl hover:bg-orange-50 transition-colors shadow flex items-center gap-1.5"
          >
            <Calculator size={14} /> 계산해보기
          </Link>
        </div>
      </div>

      {/* 하단 시나리오 미리보기 */}
      <div className="bg-orange-50 px-5 py-3 flex items-center gap-4 flex-wrap">
        {scenarios.map((s, i) => (
          <Link
            key={i}
            to="/real-calc"
            className="flex items-center gap-1.5 text-orange-700 hover:text-orange-900 transition-colors"
          >
            <span className="text-base">{s.emoji}</span>
            <span className="text-xs font-bold">{s.label}</span>
          </Link>
        ))}
        <span className="ml-auto text-xs text-orange-400">총 5가지 시나리오</span>
      </div>
    </div>
  )
}

// ── 1% 성장 시각화 진입 배너 ─────────────────────────────────────────────────
function GrowthBanner({ streak }: { streak: number }) {
  const flame = streak === 0 ? '🌱' : streak < 3 ? '🔥' : streak < 7 ? '🔥🔥' : '🔥🔥🔥'
  const growthX = Math.pow(1.01, Math.max(streak, 1)).toFixed(2)

  return (
    <div className="rounded-2xl overflow-hidden shadow-md border border-emerald-100">
      <div
        className="p-5 text-white"
        style={{ background: 'linear-gradient(135deg, #10b981 0%, #0ea5e9 100%)' }}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-11 h-11 rounded-2xl bg-white/20 flex items-center justify-center text-2xl flex-shrink-0">
              📈
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-black text-base">1% 성장 시각화</p>
                {streak > 0 && (
                  <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">
                    {flame} {streak}일 연속 → 이론상 {growthX}배
                  </span>
                )}
              </div>
              <p className="text-white/70 text-xs mt-0.5">
                매일 1%씩 성장하면 1년 후 37배 — 복리의 마법을 확인해보세요
              </p>
            </div>
          </div>
          <Link
            to="/growth"
            className="flex-shrink-0 bg-white text-emerald-600 font-black text-sm px-4 py-2 rounded-xl hover:bg-emerald-50 transition-colors shadow flex items-center gap-1.5"
          >
            <TrendingUp size={14} /> 내 성장 보기
          </Link>
        </div>
      </div>

      <div className="bg-emerald-50 px-5 py-2.5 flex items-center gap-6 flex-wrap">
        {[
          { emoji: '📊', label: '복리 성장 시뮬레이터' },
          { emoji: '📅', label: '나의 학습 달력' },
          { emoji: '🎯', label: '오늘의 1개 목표' },
          { emoji: '💬', label: '동기부여 문장' },
        ].map((item, i) => (
          <Link key={i} to="/growth"
            className="flex items-center gap-1.5 text-emerald-700 hover:text-emerald-900 transition-colors">
            <span className="text-base">{item.emoji}</span>
            <span className="text-xs font-bold">{item.label}</span>
          </Link>
        ))}
      </div>
    </div>
  )
}

// ── 5분 챌린지 진입 배너 ─────────────────────────────────────────────────
function ChallengeBanner() {
  const todayKey = 'challenge_done_' + new Date().toISOString().slice(0, 10)
  const doneToday = (() => { try { return !!localStorage.getItem(todayKey) } catch { return false } })()

  return (
    <div className="rounded-2xl overflow-hidden shadow-md border border-indigo-100">
      <div
        className="p-5 text-white"
        style={{ background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 50%, #ec4899 100%)' }}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-11 h-11 rounded-2xl bg-white/20 flex items-center justify-center text-2xl flex-shrink-0">
              ⏱️
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-black text-base">5분 챌린지</p>
                {doneToday
                  ? <span className="text-xs bg-emerald-400/30 text-emerald-200 px-2 py-0.5 rounded-full">✅ 오늘 완료!</span>
                  : <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">매일 80~140 XP</span>
                }
              </div>
              <p className="text-white/70 text-xs mt-0.5">
                {doneToday
                  ? '오늘 챌린지를 완료했어요! 내일 또 도전하세요 👋'
                  : '딱 5분만 투자하세요 — 타이머 + OX 퀴즈 + 동기부여 메시지'}
              </p>
            </div>
          </div>
          <Link
            to="/challenge"
            className="flex-shrink-0 bg-white text-indigo-600 font-black text-sm px-4 py-2 rounded-xl hover:bg-indigo-50 transition-colors shadow flex items-center gap-1.5"
          >
            <Flame size={14} /> {doneToday ? '결과 보기' : '5분 시작'}
          </Link>
        </div>
      </div>

      <div className="bg-indigo-50 px-5 py-2.5 flex items-center gap-6 flex-wrap">
        {[
          { emoji: '⏱️', label: '5분 카운트다운' },
          { emoji: '⚡', label: 'OX 퀴즈 +20XP' },
          { emoji: '🏆', label: '완료 시 80XP~' },
          { emoji: '💬', label: '동기부여 메시지' },
        ].map((item, i) => (
          <Link key={i} to="/challenge"
            className="flex items-center gap-1.5 text-indigo-700 hover:text-indigo-900 transition-colors">
            <span className="text-base">{item.emoji}</span>
            <span className="text-xs font-bold">{item.label}</span>
          </Link>
        ))}
      </div>
    </div>
  )
}
